/**
 * Coupon information from vendor (affiliate or scraped).
 */
export type VendorCouponInfo = {
  coupon_code: string | null;
  coupon_description: string | null;
  scraped_coupon_code: string | null;
  scraped_coupon_discount: string | null;
};

/**
 * Active coupon selected for display and use.
 */
export type ActiveCoupon = {
  code: string | null;
  description: string | null;
  source: 'scraped' | 'affiliate' | null;
};

/**
 * Select the best coupon from vendor's affiliate and scraped coupons.
 * 
 * Logic:
 * - If both coupons have numeric percentages, choose the one with higher percentage
 * - If only one has a numeric percentage, choose that one
 * - If neither has a numeric percentage, prefer affiliate coupon if it exists
 * - If no coupons exist, return nulls
 * 
 * @param vendor - Vendor coupon information
 * @returns ActiveCoupon with the selected coupon code, description, and source
 */
export function selectBestCoupon(vendor: VendorCouponInfo): ActiveCoupon {
  // Parse numeric discount from strings like "10% Off Order", "15% off", etc.
  const parsePercent = (text?: string | null): number | null => {
    if (!text) return null;
    const match = text.match(/(\d+)\s*%/i);
    if (!match) return null;
    return parseInt(match[1], 10);
  };

  const myPercent = parsePercent(vendor.coupon_description);
  const scrapedPercent = parsePercent(vendor.scraped_coupon_discount);

  const hasMyCoupon = !!vendor.coupon_code;
  const hasScrapedCoupon = !!vendor.scraped_coupon_code;

  // Case 1: both coupons exist and both have percents → pick higher
  if (hasMyCoupon && hasScrapedCoupon && myPercent !== null && scrapedPercent !== null) {
    if (scrapedPercent > myPercent) {
      return {
        code: vendor.scraped_coupon_code,
        description: vendor.scraped_coupon_discount,
        source: 'scraped',
      };
    } else {
      return {
        code: vendor.coupon_code,
        description: vendor.coupon_description,
        source: 'affiliate',
      };
    }
  }

  // Case 2: only scraped has a numeric percent
  if (hasScrapedCoupon && scrapedPercent !== null && (!hasMyCoupon || myPercent === null)) {
    return {
      code: vendor.scraped_coupon_code,
      description: vendor.scraped_coupon_discount,
      source: 'scraped',
    };
  }

  // Case 3: only my coupon has a numeric percent
  if (hasMyCoupon && myPercent !== null && (!hasScrapedCoupon || scrapedPercent === null)) {
    return {
      code: vendor.coupon_code,
      description: vendor.coupon_description,
      source: 'affiliate',
    };
  }

  // Case 4: no % could be parsed from either — default preference:
  // Prefer my affiliate coupon if it exists, otherwise scraped.
  if (hasMyCoupon) {
    return {
      code: vendor.coupon_code,
      description: vendor.coupon_description,
      source: 'affiliate',
    };
  }
  if (hasScrapedCoupon) {
    return {
      code: vendor.scraped_coupon_code,
      description: vendor.scraped_coupon_discount,
      source: 'scraped',
    };
  }

  // No coupon at all
  return { code: null, description: null, source: null };
}

